import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Form as UiForm, InputGroup } from '@/components/ui';
import type { Form } from '../../../services/formService';
import type { Vehicle } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';

const VEHICLE_UNIT_FIELD_NAMES = ['VehicleUnitNumber 1', 'SecondaryUnitNumber'] as const;
const SCOPED_PAGE_SIZE = 500;

export function isVehicleUnitFieldName(fieldName: string): boolean {
  return (VEHICLE_UNIT_FIELD_NAMES as readonly string[]).includes(fieldName);
}

function rateFieldNameForUnitField(fieldName: string): string | null {
  if (fieldName === 'VehicleUnitNumber 1') return 'VehicleRate';
  if (fieldName === 'SecondaryUnitNumber') return 'SecondaryVehicleRate';
  return null;
}

function findFieldIdInSameTaskGroup(
  form: Form,
  sourceFieldId: number,
  targetField: string
): number | undefined {
  const source = form.fields.find((f) => f.id === sourceFieldId);
  if (!source) return undefined;
  const groupKey = source.path.split('.').slice(0, -1).join('.');
  const target = form.fields.find(
    (f) =>
      f.field === targetField && f.path.split('.').slice(0, -1).join('.') === groupKey
  );
  return target?.id;
}

function formatVehicleRow(v: Vehicle): string {
  const desc = v.description?.trim();
  const parts = [v.unitNumber, desc].filter(Boolean);
  return parts.join(' – ');
}

interface VehicleUnitEditableFieldProps {
  fieldId: number;
  fieldName: string;
  value: string;
  onSave: (
    fieldId: number,
    value: string,
    additionalUpdates?: { fieldId: number; value: string }[]
  ) => Promise<void>;
  form: Form;
  disabled?: boolean;
  homeForemanAreaId?: number;
}

const VehicleUnitEditableField: React.FC<VehicleUnitEditableFieldProps> = ({
  fieldId,
  fieldName,
  value,
  onSave,
  form,
  disabled = false,
  homeForemanAreaId
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [scopedVehicles, setScopedVehicles] = useState<Vehicle[]>([]);
  const [scopedLoading, setScopedLoading] = useState(false);
  const [scopedError, setScopedError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Vehicle[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (!isEditing) setEditValue(value);
  }, [value, isEditing]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadScoped = useCallback(async () => {
    setScopedLoading(true);
    setScopedError(null);
    try {
      const filters: Parameters<typeof vehicleService.getVehiclesPaginated>[2] = {
        visibleToDrivers: '1',
        ...(homeForemanAreaId != null && homeForemanAreaId > 0
          ? { contextForemanAreaId: String(homeForemanAreaId) }
          : {})
      };
      const res = await vehicleService.getVehiclesPaginated(1, SCOPED_PAGE_SIZE, filters);
      setScopedVehicles(res.data || []);
    } catch (e: unknown) {
      setScopedError(e instanceof Error ? e.message : 'Failed to load vehicles');
      setScopedVehicles([]);
    } finally {
      setScopedLoading(false);
    }
  }, [homeForemanAreaId]);

  useEffect(() => {
    if (!isEditing) return;
    loadScoped();
  }, [isEditing, loadScoped]);

  useEffect(() => {
    if (!isEditing) return;
    if (debouncedSearch.length === 0) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    vehicleService
      .getVehiclesPaginated(1, 50, { search: debouncedSearch, visibleToDrivers: '1' })
      .then((res) => {
        if (!cancelled) setSearchResults(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditing, debouncedSearch]);

  const displayVehicles = useMemo(() => {
    if (debouncedSearch.length > 0) {
      return searchResults ?? [];
    }
    return scopedVehicles;
  }, [debouncedSearch.length, searchResults, scopedVehicles]);

  const legacyValueNotInList = useMemo(() => {
    const v = (value || '').trim();
    if (!v) return false;
    return !scopedVehicles.some(
      (x) => x.unitNumber === v || x.timberlineUnitNumber === v
    );
  }, [value, scopedVehicles]);

  const handleSave = async () => {
    const trimmed = editValue.trim();
    const rateName = rateFieldNameForUnitField(fieldName);
    const additional: { fieldId: number; value: string }[] = [];

    const merged = new Map<number, Vehicle>();
    for (const v of scopedVehicles) merged.set(v.id, v);
    for (const v of searchResults || []) merged.set(v.id, v);
    const match = Array.from(merged.values()).find(
      (v) =>
        v.unitNumber === trimmed ||
        (!!v.timberlineUnitNumber && v.timberlineUnitNumber === trimmed)
    );

    if (rateName) {
      const rateFieldId = findFieldIdInSameTaskGroup(form, fieldId, rateName);
      if (rateFieldId != null && match?.rate != null && match.rate !== undefined) {
        additional.push({ fieldId: rateFieldId, value: String(match.rate) });
      }
    }

    await onSave(fieldId, trimmed, additional.length ? additional : undefined);
    setIsEditing(false);
    setSearchQuery('');
    setDebouncedSearch('');
    setSearchResults(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    setSearchQuery('');
    setDebouncedSearch('');
    setSearchResults(null);
    setScopedError(null);
  };

  const beginEdit = () => {
    if (disabled) return;
    setEditValue(value);
    setSearchQuery('');
    setDebouncedSearch('');
    setSearchResults(null);
    setIsEditing(true);
  };

  if (!isEditing) {
    return (
      <div
        className="editable-cell tw-flex tw-justify-between tw-items-center"
        onClick={beginEdit}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        <span title={value || '-'}>{value || '-'}</span>
        {!disabled && <i className="bi bi-pencil tw-text-content-muted tw-opacity-50" />}
      </div>
    );
  }

  return (
    <div className="vehicle-unit-editor">
      <InputGroup className="tw-mb-1">
        <UiForm.Control
          type="text"
          size="sm"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder="Unit number"
          autoFocus
        />
      </InputGroup>
      <InputGroup className="tw-mb-1">
        <InputGroup.Text>
          <i className="bi bi-search" />
        </InputGroup.Text>
        <UiForm.Control
          type="text"
          size="sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            debouncedSearch.length > 0
              ? 'Search all assigned vehicles…'
              : 'Type to search beyond this service area…'
          }
        />
      </InputGroup>
      <div
        className="tw-border tw-border-line tw-rounded tw-text-sm tw-mb-1"
        style={{ maxHeight: 180, overflowY: 'auto', background: '#fff' }}
      >
        {scopedLoading && debouncedSearch.length === 0 && (
          <div className="tw-p-2 tw-text-content-muted">Loading vehicles for this area…</div>
        )}
        {scopedError && debouncedSearch.length === 0 && (
          <div className="tw-p-2 tw-text-danger">{scopedError}</div>
        )}
        {searchLoading && debouncedSearch.length > 0 && (
          <div className="tw-p-2 tw-text-content-muted">Searching…</div>
        )}
        {!scopedLoading &&
          debouncedSearch.length === 0 &&
          legacyValueNotInList &&
          (value || '').trim() && (
            <button
              type="button"
              className="tw-block tw-w-full tw-text-left tw-px-3 tw-py-1 tw-border-b tw-border-line tw-rounded-none hover:tw-bg-surface-hover"
              onClick={() => setEditValue((value || '').trim())}
            >
              <span className="tw-text-content-muted">Current value (not in area list): </span>
              <strong>{value}</strong>
            </button>
          )}
        {displayVehicles.length === 0 &&
          !scopedLoading &&
          !searchLoading &&
          (debouncedSearch.length > 0 || !scopedError) && (
            <div className="tw-p-2 tw-text-content-muted">
              {debouncedSearch.length > 0 ? 'No vehicles match.' : 'No vehicles in this service area.'}
            </div>
          )}
        {displayVehicles.map((v) => {
          const selected =
            editValue.trim() === v.unitNumber ||
            (!!v.timberlineUnitNumber && editValue.trim() === v.timberlineUnitNumber);
          return (
            <button
              key={v.id}
              type="button"
              className={`tw-block tw-w-full tw-text-left tw-px-3 tw-py-1 tw-border-b tw-border-line tw-rounded-none hover:tw-bg-surface-hover ${selected ? 'tw-bg-primary tw-text-white' : ''}`}
              onClick={() => setEditValue(v.unitNumber)}
            >
              {formatVehicleRow(v)}
            </button>
          );
        })}
      </div>
      <div className="tw-flex tw-gap-1 tw-justify-end">
        <Button type="button" variant="success" size="sm" onClick={handleSave}>
          <i className="bi bi-check" />
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
          <i className="bi bi-x" />
        </Button>
      </div>
    </div>
  );
};

export default VehicleUnitEditableField;
